<template>
  <nav class="sb-breadcrumbs" :aria-label="breadcrumbsTitle">
    <ul class="sb-breadcrumbs__list" ref="breadcrumbListRef">
      <li
        class="sb-breadcrumbs__item"
        v-if="hasRootItemSlot"
        :key="'root-item'"
        :aria-current="inlineItems.length === 0 ? 'page' : undefined"
      >
        <slot name="root-item" />
      </li>
      <li
        v-if="hasRootItemSlot && dropdownItems.length"
        class="sb-breadcrumbs__item"
      >
        <ChevronRight aria-hidden="true" />
        <SbBreadcrumbDropdown
          :items="dropdownItems"
          :as="linkType"
          :disable-link-navigation="disableLinkNavigation"
          @select-item="handleItemSelect"
        />
      </li>
      <template v-for="(item, index) in inlineItems" :key="item?.id">
        <template v-if="item">
          <li class="sb-breadcrumbs__item">
            <ChevronRight
              v-if="index > 0 || hasRootItemSlot"
              aria-hidden="true"
            />
            <slot name="item" v-bind="item" :item="item" :index="index">
              <SbBreadcrumbLink
                :as="item.linkType || linkType"
                :item-id="item.id"
                :href="item.href"
                :to="item.to"
                :target="item.target"
                :disabled="item.disabled"
                :current="inlineItems.length - 1 === index ? 'page' : undefined"
                :label="item.label"
                :ref="linkRefForIndex(index)"
                :data-index="index"
                :truncated="isTruncated[index]"
                :disable-link-navigation="disableLinkNavigation"
                @select-item="handleItemSelect"
              />
            </slot>
          </li>
          <li
            v-if="!hasRootItemSlot && index === 0 && dropdownItems.length"
            class="sb-breadcrumbs__item"
          >
            <ChevronRight aria-hidden="true" />
            <SbBreadcrumbDropdown
              :items="dropdownItems"
              :disable-link-navigation="disableLinkNavigation"
              @select-item="handleItemSelect"
            />
          </li>
        </template>
      </template>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import {
  ref,
  nextTick,
  watch,
  onMounted,
  onBeforeUnmount,
  computed,
  useSlots,
} from 'vue'

import type { VNodeRef, ComponentPublicInstance } from 'vue'
import type { BreadcrumbItem, BreadcrumbLinkType } from './types'

import SbBreadcrumbDropdown from './BreadcrumbDropdown.vue'
import SbBreadcrumbLink from './BreadcrumbLink.vue'

import { ChevronRight } from 'lucide-vue-next'

const slots = useSlots()

const props = withDefaults(
  defineProps<{
    items: BreadcrumbItem[]
    linkType?: BreadcrumbLinkType
    breadcrumbsTitle?: string
    disableLinkNavigation?: boolean
    minBreadcrumbWidth?: number
  }>(),
  {
    items: () => [],
    linkType: 'a',
    breadcrumbsTitle: 'Breadcrumbs',
    disableLinkNavigation: false,
    minBreadcrumbWidth: 220,
  },
)

const emit = defineEmits<{
  (
    e: 'item-click',
    payload: {
      event: MouseEvent | KeyboardEvent
      item: BreadcrumbItem
      index: number
    },
  ): void
}>()

const hasRootItemSlot = computed(() => Boolean(slots['root-item']))

function handleItemSelect(itemId: string | number | undefined): void {
  const index = props.items.findIndex((i) => String(i.id) === String(itemId))
  if (index === -1) return
  const selectedItem = props.items[index]
  if (!selectedItem) return
  handleSelect(new MouseEvent('click'), selectedItem, index)
}

function handleSelect(
  event: MouseEvent | KeyboardEvent,
  item: BreadcrumbItem,
  index: number,
) {
  if (item.disabled) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  item.onClick?.(event)
  emit('item-click', { event, item, index })
}

// --- Tooltip logic for truncation ---
const linkRefs = ref<(HTMLElement | null)[]>([])
const isTruncated = ref<boolean[]>([])

function linkRefForIndex(index: number): VNodeRef {
  return (el: Element | ComponentPublicInstance | null) => {
    let domEl: HTMLElement | null = null
    if (el && '$el' in el) {
      domEl = (el as any).$el as HTMLElement
    } else if (el instanceof HTMLElement) {
      domEl = el
    }
    setLinkRef(domEl, index)
  }
}

function setLinkRef(el: HTMLElement | null, index: number) {
  if (index === 0) linkRefs.value = []
  linkRefs.value[index] = el
}

function checkTruncation() {
  isTruncated.value = linkRefs.value.map((el) => {
    if (!el) return false
    return el.scrollWidth > el.offsetWidth
  })
}

// --- Responsive logic for breadcrumb items ---
const breadcrumbListRef = ref<HTMLElement | null>(null)
const containerWidth = ref(0)

function updateLayout() {
  if (breadcrumbListRef.value) {
    containerWidth.value = breadcrumbListRef.value.offsetWidth
  }
  nextTick(checkTruncation)
}

// Watch for changes in items and update layout/truncation
watch(
  () => props.items.map((i) => i.label),
  () => updateLayout(),
  { immediate: true },
)

onMounted(() => {
  updateLayout()
  window.addEventListener('resize', updateLayout)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateLayout)
})

// --- Inline/dropdown logic ---
const maxInlineItems = computed(() => {
  const possible = Math.floor(containerWidth.value / props.minBreadcrumbWidth)
  const middleCount = hasRootItemSlot.value
    ? props.items.length - 1
    : props.items.length - 2
  return Math.max(0, Math.min(possible, middleCount))
})

const getMiddleItems = () => {
  if (props.items.length <= 1) return []
  if (hasRootItemSlot.value) return props.items.slice(0, props.items.length - 1)

  return props.items.slice(1, props.items.length - 1)
}

const dropdownItems = computed(() => {
  const middleItems = getMiddleItems()
  if (middleItems.length <= maxInlineItems.value) return []
  return middleItems.slice(0, middleItems.length - maxInlineItems.value)
})

const inlineItems = computed(() => {
  if (props.items.length <= (hasRootItemSlot.value ? 1 : 2)) return props.items
  const last = props.items[props.items.length - 1]
  const middleItems = getMiddleItems()
  const visibleMiddle =
    maxInlineItems.value > 0 ? middleItems.slice(-maxInlineItems.value) : []
  return hasRootItemSlot.value
    ? [...visibleMiddle, last]
    : [props.items[0], ...visibleMiddle, last]
})
</script>
